import axios from 'axios';

export function billingMonths(month, quarterly = false) {
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) throw new Error('Invalid month');
    if (!quarterly) return [month];
    const start = Math.floor((Number(month.slice(5)) - 1) / 3) * 3 + 1;
    return [0, 1, 2].map((offset) => month.slice(0, 4) + '-' + String(start + offset).padStart(2, '0'));
}

export async function fetchBillingPeriod(baseUrl, month, quarterly = false, includeContracts = false) {
    const results = await Promise.all(billingMonths(month, quarterly).map(async (value) => {
        const res = await axios.get(baseUrl + '/api/admin/cp-settlement/monthly-billing', {
            params: { month: value, includeEmpty: 0, includeDetails: 0, ...(includeContracts ? { includeContracts: 1 } : {}) },
            withCredentials: true,
            timeout: 30000,
        });
        if (res?.data?.code !== 1000 || !Array.isArray(res.data?.data?.rows)) {
            throw new Error('Billing query failed');
        }
        return includeContracts ? res.data.data.rows.map((row) => ({ ...row, __billingMonth: value })) : res.data.data.rows;
    }));
    return results.flat();
}

// Preserve the existing merchant-name matching contract and amount aliases.
export function billingAmounts(rows, merchantName, multiplier = 1, cpPkId = null) {
    return rows.filter((row) => cpPkId == null ? (row.cp_name ?? row.cpName ?? '') === merchantName : String(row.cp_id ?? row.cpId ?? '') === String(cpPkId))
        .reduce((total, row) => ({
            paymentCount: total.paymentCount + Number(row.payment_completed_count ?? 0),
            paymentAmount: total.paymentAmount + Math.round(Number(row.payment_completed_amount ?? row.paymentCompletedAmount ?? 0)) * multiplier,
            settlementAmount: total.settlementAmount + Math.round(Number(row.settlement_net_amount ?? row.settlementNetAmount ?? 0)) * multiplier,
            fee: total.fee + Math.round(Number(row.payment_cp_fee_amount ?? row.paymentCpFeeAmount ?? row.settlement_fee_amount ?? 0)) * multiplier,
        }), { paymentCount: 0, paymentAmount: 0, settlementAmount: 0, fee: 0 });
}

const contractFields = ['payment_completed_count', 'payment_completed_amount', 'payment_cp_fee_amount', 'settlement_net_amount'];
const validAmount = (value) => typeof value === 'number' && Number.isSafeInteger(value);
// 종결된 미승인 실패는 분류 확인 대상에서 제외된다. 승인·취소·지급
// 금액 등 분류 결과와 모순되는 다른 집계가 있으면 숨기지 않는다.
const unclassifiedFields = [
    'payment_completed_count', 'payment_completed_amount',
    'payment_canceled_count', 'payment_canceled_amount',
    'payment_net_amount', 'payment_cp_fee_amount', 'payment_pg_fee_amount', 'payment_admin_fee_amount',
    'settlement_count', 'settlement_gross_amount', 'settlement_fee_amount',
    'settlement_pg_fee_amount', 'settlement_admin_fee_amount', 'settlement_canceled_fee_refund_amount',
    'settlement_net_amount', 'settlement_transferred_fee_amount', 'settlement_transferred_pg_fee_amount',
    'settlement_transferred_admin_fee_amount', 'settlement_transferred_net_amount',
];

// 각 월의 누락·불일치를 유지하며, 서로 다른 CP의 동일 상호를 합치지 않는다.
// settlement_count는 여러 계약이 같은 부모 정산을 공유할 수 있어 합산하지 않는다.
export function contractBillingForPeriod(rows, cpPkId, months) {
    if (!/^\d+$/.test(String(cpPkId ?? ''))) return { supported: false };
    const cpRows = rows.filter((row) => String(row.cp_id ?? row.cpId ?? '') === String(cpPkId));
    if (!cpRows.some((row) => row.contractBilling?.supported === true)) {
        return cpRows.some((row) => typeof row.contractBilling?.supported !== 'boolean') ? { supported: false, unavailable: true } : { supported: false };
    }
    const contracts = new Map();
    const missingMonths = [];
    const unreconciledMonths = [];
    const unclassifiedMonths = [];
    const reconciliationUnknownMonths = [];
    const classificationUnknownMonths = [];
    const invalidContractMonths = [];
    const differences = [];
    const mark = (list, month) => { if (!list.includes(month)) list.push(month); };
    let hasUnclassifiedData = false;
    let unmappedConfirmedAbsentMonths = 0;
    months.forEach((month) => {
        const matches = cpRows.filter((row) => row.__billingMonth === month);
        const billing = matches.length === 1 ? matches[0].contractBilling : null;
        if (!billing || billing.supported !== true || !Array.isArray(billing.contracts)) {
            missingMonths.push(month);
            return;
        }
        if (billing.reconciled === false) {
            unreconciledMonths.push(month);
            if (billing.differences && typeof billing.differences === 'object') differences.push({ month, values: billing.differences });
        } else if (billing.reconciled !== true) {
            reconciliationUnknownMonths.push(month);
        } else if (billing.differences && typeof billing.differences === 'object' &&
            Object.values(billing.differences).some((value) => !validAmount(value) || value !== 0)) {
            // A nonzero or malformed difference conflicts with the success flag.
            reconciliationUnknownMonths.push(month);
            differences.push({ month, values: billing.differences });
        }
        if (billing.fullyClassified === false) unclassifiedMonths.push(month);
        else if (billing.fullyClassified !== true) classificationUnknownMonths.push(month);
        const seen = new Set();
        billing.contracts.forEach((contract) => {
            if (!contract || typeof contract !== 'object') { if (!missingMonths.includes(month)) missingMonths.push(month); return; }
            const key = contract.contractKey ?? null;
            const mapKey = key === null ? '__NULL_CONTRACT__' : key;
            if ((key !== null && (typeof key !== 'string' || !key)) || seen.has(mapKey)) { if (!missingMonths.includes(month)) missingMonths.push(month); return; }
            seen.add(mapKey);
            if (contractFields.some((field) => !validAmount(contract[field]))) mark(invalidContractMonths, month);
            if (key === 'UNMAPPED') {
                const presentFields = unclassifiedFields.filter((field) => Object.prototype.hasOwnProperty.call(contract, field));
                if (presentFields.some((field) => !validAmount(contract[field]))) mark(invalidContractMonths, month);
                // total_count includes terminal unapproved failures, so a
                // nonzero count equal to failed_count is not a discrepancy.
                // If either optional count is missing, trust fullyClassified;
                // their absence alone does not imply an unclassified payment.
                const totalCount = contract.payment_total_count;
                const failedCount = contract.payment_failed_count;
                const hasOtherTransactions = validAmount(totalCount) && validAmount(failedCount) && totalCount > failedCount;
                // Check months before summing so opposite signs do not cancel.
                if (hasOtherTransactions || presentFields.some((field) => validAmount(contract[field]) && contract[field] !== 0)) {
                    hasUnclassifiedData = true;
                    if (billing.fullyClassified === true) mark(classificationUnknownMonths, month);
                }
            }
            if (key !== null && !['PRIMARY', 'SECONDARY', 'UNMAPPED'].includes(key)) mark(classificationUnknownMonths, month);
            if (!contracts.has(mapKey)) contracts.set(mapKey, { contractKey: key, contractName: key === null ? '-' : contract.contractName || '구분 확인 필요', months: 0, ...Object.fromEntries(contractFields.map((field) => [field, 0])) });
            const entry = contracts.get(mapKey);
            entry.months += 1;
            contractFields.forEach((field) => {
                if (entry[field] === null || !validAmount(contract[field]) || !Number.isSafeInteger(entry[field] + contract[field])) entry[field] = null;
                else entry[field] += contract[field];
            });
        });
        // Omitted UNMAPPED in an otherwise complete, fully classified month
        // contributes zero to its displayed totals, not a missing month.
        if (!seen.has('UNMAPPED') && billing.fullyClassified === true && billing.reconciled === true && !reconciliationUnknownMonths.includes(month)) unmappedConfirmedAbsentMonths += 1;
        // 미분류는 예외 항목이므로 응답에 없더라도 정상이다.
        if (!['PRIMARY', 'SECONDARY'].every((key) => seen.has(key)) && !missingMonths.includes(month)) missingMonths.push(month);
    });
    return {
        supported: true, missingMonths, unreconciledMonths, unclassifiedMonths, differences,
        reconciliationUnknownMonths, classificationUnknownMonths, invalidContractMonths,
        // Unlike general warnings, this indicates actual unclassified evidence.
        unmappedNeedsReview: Boolean(unclassifiedMonths.length || hasUnclassifiedData),
        showUnmapped: Boolean(missingMonths.length || unreconciledMonths.length || unclassifiedMonths.length || reconciliationUnknownMonths.length || classificationUnknownMonths.length || invalidContractMonths.length || hasUnclassifiedData),
        contracts: [...contracts.values()].map((entry) => ({
            ...entry,
            // 일부 월이 빠졌다면 부분 합계를 전체로 오인하지 않도록 감춘다.
            ...Object.fromEntries(contractFields.map((field) => [field, missingMonths.length || entry.months + (entry.contractKey === 'UNMAPPED' ? unmappedConfirmedAbsentMonths : 0) !== months.length ? null : entry[field]])),
        })),
    };
}

// 기존 합산 엑셀과 분리해 계약별 내역을 내보낸다. 누락은 0으로 바꾸지 않는다.
export function contractBillingCsv(rows) {
    const cell = (value) => {
        let text = value == null ? '확인 필요' : String(value);
        if (typeof value === 'string' && /^[\s\t\r\n]*[=+@-]/.test(text)) text = `'${text}`;
        return `"${text.replace(/"/g, '""')}"`;
    };
    const lines = [['CP PK', '가맹점명', '계약 구분', '결제 건수', '결제금액', '정산금액', '수수료', '집계 상태', '미분류 상태']];
    rows.filter((row) => row.contractBilling?.supported).forEach((row) => {
        const summary = row.contractBilling;
        const hasUncertainAmount = summary.contracts.some((contract) => contractFields.some((field) => !validAmount(contract[field])));
        const status = [summary.missingMonths.length ? `누락: ${summary.missingMonths.join(', ')}` : '', summary.unreconciledMonths.length ? `불일치: ${summary.unreconciledMonths.join(', ')}` : '', summary.reconciliationUnknownMonths?.length ? `대조 확인 필요: ${summary.reconciliationUnknownMonths.join(', ')}` : '', (hasUncertainAmount || summary.invalidContractMonths?.length) ? '금액 확인 필요' : ''].filter(Boolean).join(' / ') || '정상';
        const classified = summary.unclassifiedMonths.length ? `미분류: ${summary.unclassifiedMonths.join(', ')}` : summary.missingMonths.length || summary.classificationUnknownMonths?.length || summary.showUnmapped !== false ? '확인 필요' : '완전 분류';
        summary.contracts.forEach((contract) => lines.push([row.id, row.name, contract.contractName, contract.payment_completed_count, contract.payment_completed_amount, contract.settlement_net_amount, contract.payment_cp_fee_amount, status, classified]));
    });
    return '\uFEFF' + lines.map((line) => line.map(cell).join(',')).join('\r\n');
}
